import * as React from 'react';
import { Dialog, DialogContent, IconButton, Box, Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { getFontUrl } from '../../utils/fontUrls';

const MODAL_FONT_FAMILY = 'WehaSpecialCharsModalFont';

interface SpecialCharsModalProps {
  open: boolean;
  onClose: () => void;
  /** Allowed special characters as string (e.g. "*,-+"). */
  characters: string;
  /** Font identifier for rendering (e.g. "antares"). */
  fontFamily: string;
  onSelect: (char: string) => void;
}

export default function SpecialCharsModal({
  open,
  onClose,
  characters,
  fontFamily,
  onSelect,
}: SpecialCharsModalProps) {
  const theme = useTheme();
  const w = (theme as { weha?: Record<string, unknown> }).weha || {};
  const fontUrl = getFontUrl(fontFamily);
  const styleId = 'weha-special-chars-font-style';

  React.useEffect(() => {
    if (!open || !fontUrl) return;
    let el = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      document.head.appendChild(el);
    }
    el.textContent = `
      @font-face {
        font-family: '${MODAL_FONT_FAMILY}';
        src: url('${fontUrl}') format('truetype');
      }
    `;
    return () => {
      if (el?.parentNode) el.parentNode.removeChild(el);
    };
  }, [open, fontUrl]);

  const chars = React.useMemo(() => {
    const raw = (characters || '').split('').filter((c) => c.trim() !== '' || c === ' ');
    return Array.from(new Set(raw));
  }, [characters]);

  const handleSelect = (char: string) => {
    onSelect(char);
    onClose();
  };

  const wehaBlue = (w.primary as string) || theme.palette.primary.main;
  const buttonBorder = (w.borderLight as string) || theme.palette.divider;
  const appFont = theme.typography?.fontFamily || '"Open Sans", "Helvetica Neue", Arial, sans-serif';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        {/* Full-bleed blue header; DialogContent has padding 0 so header touches edges */}
        <Box
          component="div"
          style={{
            margin: 0,
            padding: '16px 24px',
            backgroundColor: wehaBlue,
            fontFamily: appFont,
            fontSize: '1rem',
            fontWeight: 600,
            position: 'relative',
            width: '100%',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <span style={{ color: '#fff', fontFamily: appFont }}>{t('weha.specialChars.title')}</span>
          <IconButton
            aria-label={t('weha.specialChars.close')}
            onClick={onClose}
            size="small"
            style={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
          >
            ×
          </IconButton>
        </Box>
        <Box style={{ padding: 24 }}>
          {chars.length === 0 ? (
            <Box py={2} style={{ color: w.textMuted || theme.palette.text.secondary }}>
              {t('weha.specialChars.none')}
            </Box>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={8} justifyContent="flex-start" alignContent="flex-start">
              {chars.map((char, index) => (
                <Box
                  key={`${char}-${index}`}
                  component="button"
                  type="button"
                  onClick={() => handleSelect(char)}
                  style={{
                    fontFamily: fontUrl ? `'${MODAL_FONT_FAMILY}', sans-serif` : 'sans-serif',
                    fontSize: 32,
                    minWidth: 56,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${buttonBorder}`,
                    borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
                    background: w.paperBackground || theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    cursor: 'pointer',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = wehaBlue;
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor =
                      (w.paperBackground as string) || theme.palette.background.paper;
                    e.currentTarget.style.color = theme.palette.text.primary as string;
                  }}
                >
                  {char === ' ' ? '␣' : char}
                </Box>
              ))}
            </Box>
          )}
          <Box display="flex" justifyContent="flex-end" marginTop={3}>
            <Button
              variant="outlined"
              onClick={onClose}
              style={{
                borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
                fontFamily: appFont,
                color: theme.palette.primary.contrastText,
                border: 'none',
              }}
            >
              {t('weha.specialChars.close')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
